import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface LivePricingTableProps {
  code: string;
  codeType: string;
}

/**
 * Live pricing table widget that fetches current pricing data from the database
 * and displays aggregated averages by setting and dosage.
 *
 * This component is embedded in educational HTML content and automatically
 * updates when pricing data changes.
 */
const LivePricingTable: React.FC<LivePricingTableProps> = ({ code, codeType }) => {
  const { data: pricingData, isLoading } = useQuery({
    queryKey: ['live-pricing', code, codeType],
    queryFn: async () => {
      // Determine which pricing averages table to use
      let pricingTable = 'cpt_pricing_averages';
      let codeColumn = 'cpt_code';
      let avgColumn = 'avg_gross_charge';
      let medianColumn = 'median_gross_charge';
      let minColumn = 'min_gross_charge';
      let maxColumn = 'max_gross_charge';

      const codeTypeLower = codeType.toLowerCase();
      if (codeTypeLower === 'cpt') {
        pricingTable = 'cpt_pricing_averages';
        codeColumn = 'cpt_code';
      } else if (codeTypeLower === 'hcpcs') {
        pricingTable = 'hcpcs_pricing_averages';
        codeColumn = 'hcpcs_code';
      } else if (codeTypeLower === 'drg') {
        pricingTable = 'drg_pricing_averages';
        codeColumn = 'drg_code';
      } else if (codeTypeLower === 'ndc' || codeTypeLower === 'drugs') {
        pricingTable = 'drug_pricing_averages';
        codeColumn = 'drug_code';
        avgColumn = 'avg_charge';
        medianColumn = 'median_charge';
        minColumn = 'min_charge';
        maxColumn = 'max_charge';
      } else if (codeTypeLower === 'supply') {
        pricingTable = 'supply_pricing_averages';
        codeColumn = 'supply_code';
        avgColumn = 'avg_charge';
        medianColumn = 'median_charge';
        minColumn = 'min_charge';
        maxColumn = 'max_charge';
      } else if (codeTypeLower === 'rev' || codeTypeLower === 'revenue') {
        pricingTable = 'revenue_pricing_averages';
        codeColumn = 'revenue_code';
        avgColumn = 'avg_charge';
        medianColumn = 'median_charge';
        minColumn = 'min_charge';
        maxColumn = 'max_charge';
      }

      const { data, error } = await supabase
        .from(pricingTable)
        .select(`
          setting_type,
          normalized_unit,
          ${avgColumn},
          ${medianColumn},
          ${minColumn},
          ${maxColumn},
          total_hospitals
        `)
        .eq(codeColumn, code)
        .order('setting_type')
        .order('normalized_unit');

      if (error) throw error;

      return data?.map(row => ({
        setting: row.setting_type || 'Unspecified',
        unit: row.normalized_unit || 'Standard',
        avgPrice: row[avgColumn] || 0,
        medianPrice: row[medianColumn] || 0,
        minPrice: row[minColumn] || 0,
        maxPrice: row[maxColumn] || 0,
        hospitalCount: row.total_hospitals || 0
      })) || [];
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  if (isLoading) {
    return (
      <div className="space-y-2 my-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (!pricingData || pricingData.length === 0) {
    return (
      <div className="border border-matrix-green/20 rounded-lg p-4 my-4 text-center bg-black/20">
        <p className="text-sm text-muted-foreground">
          Pricing data is being calculated from hospital transparency reports. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <div className="my-6 border border-matrix-green/20 rounded-lg overflow-hidden bg-black/20">
      <div className="bg-black/60 px-4 py-3 border-b border-matrix-green/20">
        <h4 className="font-mono font-bold text-matrix-green text-sm">
          💰 Live Pricing Data (Updated Daily)
        </h4>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-matrix-green/20">
            <TableHead className="text-matrix-green font-mono text-xs">Care Setting</TableHead>
            <TableHead className="text-matrix-green font-mono text-xs">Dosage/Unit</TableHead>
            <TableHead className="text-matrix-green font-mono text-xs text-right">Hospitals</TableHead>
            <TableHead className="text-matrix-green font-mono text-xs text-right">Average</TableHead>
            <TableHead className="text-matrix-green font-mono text-xs text-right">Median</TableHead>
            <TableHead className="text-matrix-green font-mono text-xs text-right">Range</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pricingData.map((row, idx) => {
            const spread = row.maxPrice - row.minPrice;
            const spreadDenominator = row.medianPrice > 0 ? row.medianPrice : row.avgPrice;
            const spreadPct = spreadDenominator > 0 ? ((spread / spreadDenominator) * 100).toFixed(0) : null;

            return (
              <TableRow key={idx} className="border-matrix-green/10 hover:bg-matrix-green/5">
                <TableCell className="font-mono text-xs">
                  {row.setting === 'inpatient' ? '🏥 Inpatient' :
                   row.setting === 'outpatient' ? '🚶 Outpatient' :
                   '❓ Unspecified'}
                </TableCell>
                <TableCell className="font-mono text-xs">{row.unit}</TableCell>
                <TableCell className="font-mono text-xs text-right">{row.hospitalCount}</TableCell>
                <TableCell className="font-mono font-bold text-xs text-right text-matrix-green">
                  ${row.avgPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell className="font-mono font-bold text-xs text-right text-blue-400">
                  ${row.medianPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell className="font-mono text-xs text-right">
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-muted-foreground">
                      ${row.minPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })} - ${row.maxPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <Badge variant={spreadPct !== null && parseInt(spreadPct) > 100 ? "destructive" : "secondary"} className="text-xs">
                      {spreadPct !== null ? `${spreadPct}% spread` : "—"}
                    </Badge>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className="bg-black/40 px-4 py-2 text-xs text-muted-foreground border-t border-matrix-green/20">
        💡 Data sourced from {pricingData.reduce((sum, row) => sum + row.hospitalCount, 0)} hospital transparency reports.
        Prices updated daily.
      </div>
    </div>
  );
};

export default LivePricingTable;
